import { config } from '@terminal/config';
import { useRemoteStorage } from '@terminal/hooks/use-remote-storage/useRemoteStorage';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { create } from '@terminal/utils/store/createStore';
import { isWithinInterval } from 'date-fns/isWithinInterval';
import { PostHog } from 'posthog-js';

export enum SupportedLanguages {
  EN = 'en',
  JA = 'ja',
  PT = 'pt',
  KO = 'ko',
}

interface FlagsLanguageEnabled {
  enabled: boolean;
}

export interface FlagsMaintenance {
  from: string;
  to: string;
}

export type FlagsLanguage = {
  [key in SupportedLanguages]?: FlagsLanguageEnabled;
};

export interface SystemFlags {
  maintenance?: FlagsMaintenance;
  languages?: FlagsLanguage;
  feature_web: FeatureFlag[];
}

const flagsEndpoint = `system/flags/v1`;

export const FEATURE_FLAGS = [
  'sample',
  'hide_alfred_download_button',
  'alfred_daily_brief_cards',
  'alfred_user_upload',
  'alfred_trusted_sources',
  'live_candles_feature_flag',
  'drawer_download',
  'scheduled_task_feature',
  'snbc_user',
  'msf_connection',
  'enable_chartv2',
  'theme_intraday_chart',
  'DATA-1398-api_entity_screener_v4_entities',
  'prompt_refinement',
  'enable_doc_search',
  'catalyst_generation',
  'inline_citations_feature_flag',
  'theme_v3',
  'alfred_ask_analyst',
  'PLATFORM-4174-alert_fast_follow_insights_page',
  'REASONER-1790-line-chart-convert',
  'REASONER-1791-bar-chart-converter',
  'REASON-1901-provenance-bundle-download',
  'REASON-1793-donut-chart-converter',
  'PLATFORM-4342-korean_language',
  'REASON-1872-waterfall-converter',
  'PLATFORM-4383_onboarding_redesign',
  'REASON-1792-dot-chart-converter',
  'REASON-1794-heatmap-converter',
  'REASON-1897-conversation-v3',
  'REASON-2054-chart-setting',
] as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[number];

const featureFlagSet = new Set<string>(FEATURE_FLAGS);

const isFeatureFlag = (value: string): value is FeatureFlag =>
  featureFlagSet.has(value);

interface SystemFlagsStore {
  systemFlags: Omit<SystemFlags, 'feature_web'>;
  featureFlags: FeatureFlag[];
  posthogClient?: PostHog;
  initialise(posthogClient?: PostHog): Promise<void>;
  checkIsInMaintenance(): boolean;
  getFeatureFlagState: (flag: FeatureFlag) => boolean;
}
const configFlags = config.app.featureFlags.filter(isFeatureFlag);
export const useFlags = create<SystemFlagsStore>((set, get) => ({
  systemFlags: {},
  getFeatureFlagState: (flag: FeatureFlag) => get().featureFlags.includes(flag),
  featureFlags: configFlags,
  initialise: async posthogClient => {
    const systemFlags = await apiFetch<SystemFlags>(flagsEndpoint);

    if (posthogClient) {
      set({
        posthogClient,
      });

      posthogClient.onFeatureFlags(() => {
        const posthogFlags = posthogClient.featureFlags.getFlagsWithDetails();

        set(state => {
          const posthogFlagKeys = new Set(Object.keys(posthogFlags));
          const enabledFlags = Object.entries(posthogFlags)
            .filter(([_, details]) => details.enabled)
            .map(([key]) => key)
            .filter(isFeatureFlag);

          const nonPosthogFlags = state.featureFlags.filter(
            flag => !posthogFlagKeys.has(flag)
          );

          return {
            featureFlags: [...nonPosthogFlags, ...enabledFlags],
          };
        });
      });

      const flagsWithDetails = posthogClient.featureFlags.getFlagsWithDetails();

      // Flags may be empty after re-authentication; reload if needed
      if (!flagsWithDetails || Object.keys(flagsWithDetails).length === 0) {
        posthogClient.reloadFeatureFlags();
      }
    }

    if (!systemFlags.error) {
      const { feature_web: featureWeb, ...rest } = systemFlags.data;

      const remoteStorageFeatureFlags =
        useRemoteStorage.getState().items?.feature_flags ?? {};
      const remoteStorageFlags = Object.entries(remoteStorageFeatureFlags)
        .filter(([_, value]) => value === 'true')
        .map(([key]) => key)
        .filter(isFeatureFlag);

      set(state => ({
        systemFlags: rest,
        featureFlags: [
          ...new Set([
            ...featureWeb,
            ...remoteStorageFlags,
            ...state.featureFlags,
          ]),
        ],
      }));
    }
  },
  checkIsInMaintenance: () => {
    const {
      systemFlags: { maintenance },
    } = get();

    if (maintenance) {
      const start = new Date(maintenance.from);
      const end = new Date(maintenance.to);
      return isWithinInterval(new Date(), {
        start,
        end,
      });
    }

    return false;
  },
}));
